import React, { useEffect, useMemo, useState } from "react";
import { AuthError, AuthenticationResult, EventType, InteractionType, IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { AuthErrorPage } from "./AuthErrorPage";
import { AuthConfig, readAuthConfig } from "./config";
import { AuthConfigContext, AuthStatus, AuthStatusContext } from "./context";
import { createMsalInstance } from "./msal";
import { useAuth } from "./useAuth";

export type AuthEnv = Record<string, string | undefined>;
type InstanceFactory = (config: AuthConfig) => IPublicClientApplication;

interface AuthProviderProps {
  env: AuthEnv;
  /** Factory for the MSAL instance; tests pass a fake. */
  createInstance?: InstanceFactory;
  children: React.ReactNode;
}

const ACCOUNT_EVENTS: string[] = [EventType.LOGIN_SUCCESS, EventType.ACQUIRE_TOKEN_SUCCESS];

type ConfigState = { config: AuthConfig } | { error: string };

function loadConfig(env: AuthEnv): ConfigState {
  try {
    return { config: readAuthConfig(env) };
  } catch (error) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
}

export function AuthProvider({ env, createInstance = createMsalInstance, children }: AuthProviderProps) {
  const [state] = useState(() => loadConfig(env));
  if ("error" in state) {
    return <AuthErrorPage title="Sign-in is not configured" detail={state.error} />;
  }
  return (
    <ConfiguredAuthProvider config={state.config} createInstance={createInstance}>
      {children}
    </ConfiguredAuthProvider>
  );
}

/**
 * Generic text for the sign-in failure page: error code and correlation id only.
 * Raw MSAL messages can carry AADSTS details, user names and trace ids; never shown.
 */
function describeSignInError(error: unknown): string {
  const code = error instanceof AuthError && error.errorCode ? error.errorCode : "unknown_error";
  const correlationId = error instanceof AuthError && error.correlationId ? error.correlationId : "n/a";
  return `Please try again or contact an administrator. Error code: ${code}. Correlation id: ${correlationId}.`;
}

interface ConfiguredAuthProviderProps {
  config: AuthConfig;
  createInstance: InstanceFactory;
  children: React.ReactNode;
}

function ConfiguredAuthProvider({ config, createInstance, children }: ConfiguredAuthProviderProps) {
  const [instance] = useState(() => createInstance(config));
  const [loginError, setLoginError] = useState<string | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const status = useMemo<AuthStatus>(
    () => ({ reportSessionEnded: () => setSessionEnded(true), reportForbidden: () => setForbidden(true) }),
    [],
  );

  useEffect(() => {
    const callbackId = instance.addEventCallback((message) => {
      const result = message.payload as AuthenticationResult | null;
      if (ACCOUNT_EVENTS.includes(message.eventType) && result?.account) {
        instance.setActiveAccount(result.account);
      }
      // A failed sign-in redirect (e.g. user not assigned to the admin app) ends here.
      const redirectFailed =
        message.eventType === EventType.ACQUIRE_TOKEN_FAILURE && message.interactionType === InteractionType.Redirect;
      if (redirectFailed) {
        setLoginError(describeSignInError(message.error));
      }
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  return (
    <AuthConfigContext.Provider value={config}>
      <MsalProvider instance={instance}>
        <AuthStatusContext.Provider value={status}>
          <AuthContent loginError={loginError} sessionEnded={sessionEnded} forbidden={forbidden}>
            {children}
          </AuthContent>
        </AuthStatusContext.Provider>
      </MsalProvider>
    </AuthConfigContext.Provider>
  );
}

interface AuthContentProps {
  loginError: string | null;
  sessionEnded: boolean;
  forbidden: boolean;
  children: React.ReactNode;
}

/** Never a blank app: auth failures replace the content with a page that offers a manual sign-in. */
function AuthContent({ loginError, sessionEnded, forbidden, children }: AuthContentProps) {
  const { loginWithRedirect } = useAuth();
  const signInAgain = () => void loginWithRedirect();

  if (loginError) {
    return <AuthErrorPage title="Sign-in failed" detail={loginError} onRetry={signInAgain} />;
  }
  if (sessionEnded) {
    return (
      <AuthErrorPage
        title="Your session has ended"
        detail="The admin API did not accept your sign-in. Sign in again to continue."
        onRetry={signInAgain}
      />
    );
  }
  return (
    <>
      {forbidden ? (
        <div role="status" style={{ padding: "8px 16px", background: "#fff4e5", color: "#663c00" }}>
          No admin role — ask an administrator.
        </div>
      ) : null}
      {children}
    </>
  );
}
